import { TableWithFilters } from 'components/Tables/TableWithFilters';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { t } from 'utilities/TranslationUtils';

// components
import { TableCell } from '@trackman/web-shared-components';
import { AppSwitch } from 'components/form';
import { AppPageHeader, AppSectionErrorBoundary } from 'components/ui';
import { useStores } from 'index';
import { appTableFiltersInstance, toursTableColumns } from './ToursTableColumns';

// types
import { IOrganizationTour } from '../../types/models/OrganizationTour';

export const ToursPage = observer(() => {
  const history = useHistory();
  const { apiServiceAxiosInstance: apiInstance, organizationsStore, analyticsStore, notificationStore, toursStore } = useStores();

  const { organizationId } = useParams<{ organizationId: string }>();
  const [organizationName, setOrganizationName] = useState<string>();

  useEffect(() => {
    organizationsStore.getOrganizations(apiInstance);
    toursStore.setSelectedOrganizationId(organizationId);
    toursStore.getOrganizationTours(apiInstance);
    analyticsStore.sendPageView('ToursPage');
  }, []);

  const rows = useMemo(() => toursStore.selectedOrganizationTours, [toursStore.selectedOrganizationTours]);

  const columns = useMemo(() => toursTableColumns(toursStore.selectedOrganizationTours), [toursStore.selectedOrganizationTours]);

  useEffect(() => {
    getOrganizationName();
  }, [organizationId]);

  const getOrganizationName = async () => {
    const orgName = await organizationsStore.getOrganizationName(apiInstance, organizationId);
    setOrganizationName(orgName);
  };

  const handleTourAssignment = async (e: any, tourId: string) => {
    try {
      const isChecked = e.target.checked;

      notificationStore.addToast('', isChecked ? t('toastMessages.assigning') : t('toastMessages.unassigning'), 'info');
      if (isChecked) {
        await toursStore.addTourAssignment(apiInstance, tourId);
      } else {
        await toursStore.deleteTourAssignment(apiInstance, tourId);
      }
      notificationStore.toasts.pop(); // Remove the updating toast
      notificationStore.addToast('', isChecked ? t('toastMessages.assigned') : t('toastMessages.unassigned'), 'success');
    } catch (error) {
      notificationStore.toasts.pop(); // Remove the updating toast
      notificationStore.addToast('', t('toastMessages.failedToUpdate'), 'error');
      console.log(error);
    }
  };

  const renderCell = ({ cellValue, columnHeader, columnId, row }: { cellValue: any; columnHeader: string; columnId: string; row: any }) => {

    if (columnId === "isAssigned") {
      return (
        <div className='d-flex align-items-center'>
          <AppSwitch
            name={columnHeader}
            isReversed={false}
            isChecked={cellValue}
            width={'100'}
            isDisabled={toursStore.isUpdating}
            hideValue={true}
            color='primary'
            onChange={(e) => handleTourAssignment(e, row.id)}
          />
        </div>
      );
    }

    return cellValue || '-';
  };

  const rowMapper = (cells: any[]) => {
    return (
      <>
        {cells.map(({ getCellProps, column, row, render, value }) => (
          <TableCell {...getCellProps()} className='table-body-cell' align={column.align} onClick={() => { }}>
            {renderCell({ cellValue: value, columnHeader: column.Header, columnId: column.id, row: row.original })}
          </TableCell>
        ))}
      </>
    );
  };

  return (
    <AppSectionErrorBoundary>
      <div>
        <AppPageHeader
          title={organizationName ?? ''}
          subtitle={t('organizationsPage.tours')}
          backBtn={{ isVisible: true, onClick: () => history.push('/') }}
        />

        <TableWithFilters<IOrganizationTour>
          appTableFiltersInstance={appTableFiltersInstance}
          rowMapper={rowMapper}
          columns={columns}
          data={rows}
          isLoading={toursStore.isLoading}
        />

      </div>
    </AppSectionErrorBoundary>
  );
});

export default ToursPage;
